import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./CustomSelect.css";

const CustomSelect = ({
  value,
  onChange,
  options = [],
  disabled = false,
  className = "",
  style = {},
  audittrail =false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const selectRef = useRef(null);
  const optionsRef = useRef(null);
  const [optionsHeight, setOptionsHeight] = useState(200);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position and style for portal rendering
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - selectRect.bottom;
      const spaceAbove = selectRect.top;

      let positionStyle = {
        position: "fixed",
        left: selectRect.left,
        width: selectRect.width,
        zIndex: 99999,
      };

      if (spaceBelow < optionsHeight + 10 && spaceAbove > optionsHeight + 10) {
        // Position above - align bottom of dropdown with top of select
        positionStyle.top = selectRect.top - optionsHeight - 4;
        positionStyle.bottom = "auto";
        setDropdownPosition("top");
      } else {
        // Position below - align top of dropdown with bottom of select
        positionStyle.top = selectRect.bottom + 4;
        positionStyle.bottom = "auto";
        setDropdownPosition("bottom");
      }

      setDropdownStyle(positionStyle);
    }
  }, [isOpen, selectRef, optionsHeight]);

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      if (selectRef.current) {
        const selectRect = selectRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - selectRect.bottom;
        const spaceAbove = selectRect.top;

        let positionStyle = {
          position: "fixed",
          left: selectRect.left,
          width: selectRect.width,
          zIndex: 99999,
        };

        if (spaceBelow < optionsHeight + 10 && spaceAbove > optionsHeight + 10) {
          positionStyle.top = selectRect.top - optionsHeight - 4;
          positionStyle.bottom = "auto";
        } else {
          positionStyle.top = selectRect.bottom + 4;
          positionStyle.bottom = "auto";
        }

        setDropdownStyle(positionStyle);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen, optionsHeight]);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdownContent = isOpen && !disabled && (
    <div
      ref={(el) => {
        optionsRef.current = el;
        if (el) {
          const height = el.getBoundingClientRect().height;
          if (height > 0 && height !== optionsHeight) {
            setOptionsHeight(height);
          }
        }
      }}
      className={`custom-select-options custom-select-options-${dropdownPosition}`}
      style={dropdownStyle}
    >
      {options.map((option) => (
        <div
          key={option.value}
          className={`custom-select-option ${
            value === option.value ? "selected" : ""
          }`}
          onClick={() => handleSelect(option.value)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div
        ref={selectRef}
        className={`custom-select-wrapper ${className}`}
        style={{ ...style, position: "relative" }}
      >
        <div
          className={`custom-select-header ${disabled ? "disabled" : ""} ${isOpen ? "open" : ""}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className="custom-select-value">
            {selectedOption?.label || value || "Select..."}
          </span>
          {(!disabled || audittrail) && (
          <span className={`custom-select-arrow ${isOpen ? "open" : ""}`}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="#666"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          )}
        </div>
      </div>
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </>
  );
};

export default CustomSelect;

